import React, { createContext, useContext, useEffect, useState } from "react";
import { ErrorSnackbar, SuccessSnackbar, WarningSnackbar } from "../components/Snackbars";

interface EmployeeSnackbarMessage {
    message: string;
    type: "success" | "error" | "warning";
}

export interface EmployeeSnackbarContextProps {
    showMessage: (message: string, type?: "success" | "error" | "warning") => void;
}

const SnackbarContext = createContext<EmployeeSnackbarContextProps | undefined>(undefined);

export const useEmployeeSnackbar = () => {
    const context = useContext(SnackbarContext);
    if (!context) {
        throw new Error("useSnackbar debe usarse dentro de un SnackbarProvider");
    }
    return context;
};

export const EmployeeSnackbarProvider: React.FC = ({ children }) => {
    const [messageQueue, setMessageQueue] = useState<EmployeeSnackbarMessage[]>([]);
    const [currentMessage, setCurrentMessage] = useState<EmployeeSnackbarMessage | null>(null);
    const [open, setOpen] = useState(false);

    const showMessage = (message: string, type: "success" | "error" | "warning" = "warning") => {
        const newMessage: EmployeeSnackbarMessage = {
            message,
            type,
        };
        setMessageQueue((prevQueue) => [...prevQueue, newMessage]);
    };

    useEffect(() => {
        if (!open && messageQueue.length > 0) {
            setCurrentMessage(messageQueue[0]);
            setOpen(true);
        }
    }, [open, messageQueue]);

    const handleClose = () => {
        setCurrentMessage(null);
        setOpen(false);
        setMessageQueue((prevQueue) => prevQueue.slice(1));
    };

    return (
        <SnackbarContext.Provider value={{ showMessage }}>
            {children}
            {currentMessage && currentMessage.type === "success" && (
                <SuccessSnackbar message={currentMessage.message} onClose={handleClose} />
            )}
            {currentMessage && currentMessage.type === "error" && (
                <ErrorSnackbar message={currentMessage.message} onClose={handleClose} />
            )}
            {currentMessage && currentMessage.type === "warning" && (
                <WarningSnackbar message={currentMessage.message} onClose={handleClose} />
            )}
        </SnackbarContext.Provider>
    );
};
